// src/components/padrekko/qc/ObligationsReport.jsx
//
// Day 6 — Compliance Obligations report. A DERIVED, read-only view across the
// caller's accessible cases (optionally narrowed by the TopNav project picker).
// It aggregates outstanding remediation items:
//   - not_completed findings carry the operator's committed target_date and
//     are aged into buckets vs today (Upcoming / Overdue 1–30 / 31–60 / 60+),
//     grouped by the responsible party.
//   - pending findings have no committed date yet — they sit in their own
//     "Awaiting a date" group and are NEVER aged or hidden.
//
// THAT GROUP WAS CALLED "No response" UNTIL 15g8, AND THE WORDS WERE TAKEN.
// 099 added a case-level non-response derivation — SHARED, DEADLINE PASSED,
// NOBODY ANSWERED — which is a materially worse fact than a finding with no
// committed date yet, and 15g5's rollup reports it as a case state. Two
// different claims under one label, on one page, would have been the same
// defect 098 and 099 spent two migrations avoiding in the data: no token
// appears in both vocabularies, so a value names its own thing.
//
// THE LABEL MOVED, THE WIRE DID NOT. The API key stays `no_response` —
// renaming it would break every client mid-flight for a copy fix, and the
// endpoint that produces it is outside this change's blast radius. The
// mismatch is deliberate and is filed as REM-305.
// Per-case it also surfaces the RECOMMENDED next-observation date.
//
// HONESTY: this is a recommendation report generated from committed remediation
// deadlines, NOT an autonomous scheduler. Copy must never imply auto-execution.
//
// Mounted as a fixed overlay from QcPicker. Self-contained: reads useQcStore +
// useProjectStore directly, loads on open and on project change. PDF export
// reuses the app-wide #qc-print-host portal + @media print rules (index.css).

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X as XIcon, Printer, Loader2, RefreshCw, CalendarClock } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { useProjectStore } from "../../../stores/useProjectStore";

// Bucket display metadata — order is the canonical summary-header order.
const BUCKETS = [
  { key: "upcoming",        label: "Upcoming",       tone: "bg-sky-500/15 border-sky-500/40 text-sky-200" },
  { key: "overdue_1_30",    label: "Overdue 1–30",   tone: "bg-amber-500/15 border-amber-500/40 text-amber-200" },
  { key: "overdue_31_60",   label: "Overdue 31–60",  tone: "bg-orange-500/15 border-orange-500/40 text-orange-200" },
  { key: "overdue_60_plus", label: "Overdue 60+",    tone: "bg-red-500/15 border-red-500/40 text-red-200" },
  // Key is `no_response` on the wire; the LABEL is "Awaiting a date" (15g8).
  // See the header — the words "No response" now belong to the case-level
  // derivation, which means something worse. REM-305 tracks the key.
  { key: "no_response",     label: "Awaiting a date", tone: "bg-slate-700/40 border-slate-600/60 text-slate-300" },
  // REM-300. Open work the owner SENT BACK. Its own chip rather than folding
  // into "Awaiting a date": the operator did respond, and was rejected. A rejected
  // closeout also carries no target_date (098's shape CHECK), so it cannot be
  // aged and does not belong in an overdue bucket either.
  { key: "rejected",        label: "Sent back",      tone: "bg-rose-500/15 border-rose-500/40 text-rose-200" },
];
const BUCKET_BY_KEY = Object.fromEntries(BUCKETS.map((b) => [b.key, b]));

// Aging phrase for a not_completed item. days_overdue: >0 overdue, <=0 upcoming.
function agePhrase(days) {
  const n = Number(days);
  if (!Number.isFinite(n)) return "";
  if (n <= 0) return n === 0 ? "due today" : `due in ${-n}d`;
  return `${n}d overdue`;
}

function BasisLabel({ basis }) {
  if (basis === "deadline") return "based on outstanding remediation deadlines";
  if (basis === "routine") return "routine re-observation cadence";
  return "not set";
}

export default function ObligationsReport({ onClose }) {
  const obligations        = useQcStore((s) => s.obligations);
  const obligationsLoading  = useQcStore((s) => s.obligationsLoading);
  const obligationsError    = useQcStore((s) => s.obligationsError);
  const loadObligations     = useQcStore((s) => s.loadObligations);
  const activeProjectId     = useProjectStore((s) => s.activeProjectId);
  const projects            = useProjectStore((s) => s.projects);

  // Load on open + whenever the active project changes (mirrors QcPicker).
  useEffect(() => {
    loadObligations({ project: activeProjectId });
  }, [loadObligations, activeProjectId]);

  const scopeLabel =
    activeProjectId && activeProjectId !== "global"
      ? (projects.find((p) => p.id === activeProjectId)?.name ?? "Selected project")
      : "All projects";

  /* ---- PDF export (reuse #qc-print-host portal + @media print rules) ---- */
  const [printing, setPrinting] = useState(false);
  const prevTitleRef = useRef(null);

  function startPrint() {
    if (printing || !obligations) return;
    const ymd = new Date().toISOString().slice(0, 10);
    prevTitleRef.current = document.title;
    document.title = `Compliance Obligations - ${scopeLabel} - ${ymd}`;
    setPrinting(true);
  }

  useEffect(() => {
    if (!printing) return;
    const cleanup = () => {
      setPrinting(false);
      if (prevTitleRef.current !== null) {
        document.title = prevTitleRef.current;
        prevTitleRef.current = null;
      }
    };
    window.addEventListener("afterprint", cleanup);
    const rafId = requestAnimationFrame(() => window.print());
    const fallback = setTimeout(cleanup, 60_000);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(fallback);
      window.removeEventListener("afterprint", cleanup);
    };
  }, [printing]);

  const summary = obligations?.summary ?? null;
  const groups = obligations?.groups ?? [];
  const noResponse = obligations?.no_response ?? [];
  const cases = obligations?.cases ?? [];
  const isEmpty =
    !obligationsLoading && !obligationsError && summary && summary.total === 0;

  return (
    <>
      <div className="fixed inset-0 z-[120] flex flex-col bg-slate-950/95 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-800 bg-slate-900/80">
          <div className="min-w-0">
            <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
              Compliance Obligations
            </div>
            <div className="text-[13px] text-slate-100 font-medium truncate flex items-center gap-2">
              {scopeLabel}
              <span className="text-[9px] text-fg-secondary font-normal">
                Recommended next observations — derived from committed remediation deadlines
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => loadObligations({ project: activeProjectId })}
              disabled={obligationsLoading}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         border-slate-700 text-slate-300 hover:bg-slate-800
                         disabled:opacity-50 transition"
              title="Refresh"
            >
              <RefreshCw size={11} className={obligationsLoading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={startPrint}
              disabled={printing || !obligations || isEmpty}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Export to PDF (print)"
            >
              {printing ? <Loader2 size={11} className="animate-spin" /> : <Printer size={11} />}
              PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-7 h-7 rounded border
                         border-slate-700 text-slate-400 hover:text-slate-100
                         hover:bg-slate-800 transition"
              title="Close"
              aria-label="Close"
            >
              <XIcon size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {obligationsLoading && (
            <div className="text-[11px] text-fg-secondary italic flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading obligations…
            </div>
          )}
          {obligationsError && !obligationsLoading && (
            <div className="text-[11px] text-red-400/80">{obligationsError}</div>
          )}
          {isEmpty && (
            <div className="text-[11px] text-fg-secondary italic leading-relaxed">
              No outstanding remediation items for this scope. Every finding is either
              completed or no case in scope has open obligations.
            </div>
          )}

          {!obligationsLoading && !obligationsError && summary && summary.total > 0 && (
            <div className="max-w-3xl space-y-5">
              <SummaryHeader summary={summary} />
              <ObligationGroups groups={groups} />
              <AwaitingDateSection items={noResponse} />
              <NextObservationSection cases={cases} />
              <HonestyFooter />
            </div>
          )}
        </div>
      </div>

      {/* PDF portal — mounts under document.body so the app-wide @media print
          rules (index.css) hide the live app and show only this. */}
      {printing && obligations && createPortal(
        <div id="qc-print-host">
          <PrintReport
            scopeLabel={scopeLabel}
            summary={summary}
            groups={groups}
            noResponse={noResponse}
            cases={cases}
          />
        </div>,
        document.body
      )}
    </>
  );
}

/* ─────────────────────────── screen sections ──────────────────────────── */

function SummaryHeader({ summary }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
        Summary · {summary.total} outstanding {summary.total === 1 ? "item" : "items"}
      </div>
      <div className="flex flex-wrap gap-2">
        {BUCKETS.map((b) => (
          <div
            key={b.key}
            className={`flex items-baseline gap-1.5 text-[10px] border rounded px-2 py-1 ${b.tone}`}
          >
            <span className="font-semibold text-[13px] leading-none">{summary[b.key] ?? 0}</span>
            <span className="opacity-80">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item, showAge }) {
  const bucket = item.bucket ? BUCKET_BY_KEY[item.bucket] : null;
  return (
    <div className="flex items-center gap-2 text-[10px] py-1 border-t border-slate-800/50 first:border-t-0">
      <span className="text-slate-200 truncate flex-1" title={item.case_name}>
        {item.case_name}
      </span>
      <span className="text-fg-secondary truncate hidden sm:block">{item.pad_name}</span>
      <span className="text-fg-secondary">#{item.idx}</span>
      {showAge && bucket && (
        <span className={`font-mono uppercase tracking-wide border rounded px-1 py-[1px] text-[8px] ${bucket.tone}`}>
          {bucket.label}
        </span>
      )}
      {showAge && (
        <span className="text-slate-400 w-20 text-right shrink-0">{agePhrase(item.days_overdue)}</span>
      )}
      {showAge && (
        <span className="text-fg-secondary w-20 text-right shrink-0">{item.target_date}</span>
      )}
    </div>
  );
}

function ObligationGroups({ groups }) {
  if (!groups.length) return null;
  return (
    <div className="space-y-3">
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
        By responsible party
      </div>
      {groups.map((g) => (
        <div key={g.responsible_party} className="rounded border border-slate-800 bg-slate-900/40">
          <div className="px-2.5 py-1.5 border-b border-slate-800 flex items-center justify-between">
            <span className="text-[11px] text-slate-100 font-medium">{g.responsible_party}</span>
            <span className="text-[9px] text-fg-secondary">
              {g.items.length} {g.items.length === 1 ? "item" : "items"}
            </span>
          </div>
          <div className="px-2.5 py-1">
            {g.items.map((it) => (
              <ItemRow key={it.finding_id} item={it} showAge />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AwaitingDateSection({ items }) {
  if (!items.length) return null;
  return (
    <div className="rounded border border-slate-700/60 bg-slate-800/20">
      <div className="px-2.5 py-1.5 border-b border-slate-700/60 flex items-center justify-between">
        <span className="text-[11px] text-slate-200 font-medium">Awaiting a date</span>
        <span className="text-[9px] text-fg-secondary">
          {items.length} awaiting a committed date — not aged
        </span>
      </div>
      <div className="px-2.5 py-1">
        {items.map((it) => (
          <ItemRow key={it.finding_id} item={it} showAge={false} />
        ))}
      </div>
    </div>
  );
}

function NextObservationSection({ cases }) {
  if (!cases.length) return null;
  return (
    <div className="space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary flex items-center gap-1.5">
        <CalendarClock size={11} />
        Recommended next observation per case
      </div>
      <div className="rounded border border-slate-800 bg-slate-900/40 divide-y divide-slate-800/60">
        {cases.map((c) => (
          <div key={c.case_id} className="px-2.5 py-1.5 flex items-center gap-2 text-[10px]">
            <span className="text-slate-200 truncate flex-1" title={c.case_name}>{c.case_name}</span>
            {c.next_observation_date ? (
              <>
                <span className="text-sky-200 font-medium">{c.next_observation_date}</span>
                <span className="text-fg-secondary hidden sm:block">
                  · <BasisLabel basis={c.next_observation_basis} />
                </span>
              </>
            ) : (
              <span className="text-fg-secondary italic">not set (case still open)</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HonestyFooter() {
  return (
    <div className="text-[9px] text-fg-secondary leading-relaxed border-t border-slate-800 pt-2">
      This report is generated from committed remediation deadlines. Dates shown are
      recommended next observations — they are not automatically scheduled and no action
      is taken without a person initiating it.
    </div>
  );
}

/* ─────────────────────────── print layout ─────────────────────────────── */
// Light-on-white, table-like. Mirrors the screen grouping; no interactivity.

function PrintReport({ scopeLabel, summary, groups, noResponse, cases }) {
  const generated = new Date().toLocaleString();
  return (
    <div style={{ color: "#111", fontSize: "11px", padding: "8px 4px" }}>
      <h1 style={{ fontSize: "18px", margin: "0 0 2px" }}>Compliance Obligations</h1>
      <div style={{ color: "#555", marginBottom: "10px" }}>
        {scopeLabel} · Generated {generated}
      </div>

      <h2 style={{ fontSize: "12px", borderBottom: "1px solid #ccc", paddingBottom: "2px" }}>
        Summary — {summary.total} outstanding
      </h2>
      <div style={{ margin: "4px 0 12px" }}>
        {BUCKETS.map((b) => (
          <span key={b.key} style={{ marginRight: "14px" }}>
            <strong>{summary[b.key] ?? 0}</strong> {b.label}
          </span>
        ))}
      </div>

      {groups.map((g) => (
        <div key={g.responsible_party} style={{ marginBottom: "10px" }}>
          <h3 style={{ fontSize: "11px", margin: "0 0 2px" }}>
            {g.responsible_party} ({g.items.length})
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {g.items.map((it) => (
                <tr key={it.finding_id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: "2px 4px" }}>{it.case_name}</td>
                  <td style={{ padding: "2px 4px", color: "#666" }}>{it.pad_name}</td>
                  <td style={{ padding: "2px 4px", color: "#666" }}>#{it.idx}</td>
                  <td style={{ padding: "2px 4px" }}>{BUCKET_BY_KEY[it.bucket]?.label}</td>
                  <td style={{ padding: "2px 4px", textAlign: "right" }}>{agePhrase(it.days_overdue)}</td>
                  <td style={{ padding: "2px 4px", textAlign: "right", color: "#666" }}>{it.target_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {noResponse.length > 0 && (
        <div style={{ marginBottom: "10px" }}>
          <h3 style={{ fontSize: "11px", margin: "0 0 2px" }}>
            Awaiting a date ({noResponse.length}) — no committed date yet, not aged
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {noResponse.map((it) => (
                <tr key={it.finding_id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: "2px 4px" }}>{it.case_name}</td>
                  <td style={{ padding: "2px 4px", color: "#666" }}>{it.pad_name}</td>
                  <td style={{ padding: "2px 4px", color: "#666" }}>#{it.idx}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* GUARDED, LIKE ITS SCREEN COUNTERPART. The screen section opens with
          `if (!cases.length) return null`; this one had no guard at all, so an
          empty `cases` rendered the heading over an empty table.

          That matters more here than it would on screen: this is the PDF, the
          artifact that LEAVES THE SYSTEM. A respondent's copy would have
          carried a section title promising per-case scheduling with nothing
          beneath it — not a false claim, but a customer-facing document that
          looks truncated.

          `cases` is now empty for a respondent by construction: the server
          omits a case's entry when the caller's role ON THAT CASE is
          operator_analyst, because next_observation_* is the owner's decision.
          A caller who owns one case and responds on another still gets the one
          they own, and this section with it. */}
      {cases.length > 0 && (
      <>
      <h2 style={{ fontSize: "12px", borderBottom: "1px solid #ccc", paddingBottom: "2px" }}>
        Recommended next observation per case
      </h2>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "12px" }}>
        <tbody>
          {cases.map((c) => (
            <tr key={c.case_id} style={{ borderTop: "1px solid #eee" }}>
              <td style={{ padding: "2px 4px" }}>{c.case_name}</td>
              <td style={{ padding: "2px 4px" }}>
                {c.next_observation_date || "not set (case still open)"}
              </td>
              <td style={{ padding: "2px 4px", color: "#666" }}>
                <BasisLabel basis={c.next_observation_basis} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </>
      )}

      <div style={{ color: "#888", fontSize: "9px", borderTop: "1px solid #ccc", paddingTop: "4px" }}>
        This report is generated from committed remediation deadlines. Dates shown are
        recommended next observations — they are not automatically scheduled and no action
        is taken without a person initiating it.
      </div>
    </div>
  );
}
